import { useUpdateEffect } from 'ahooks';
import { FaCalendarAlt } from '@meronex/icons/fa';
import dayjs from 'dayjs';
import { uniqueId } from 'lodash-es';
import React, { FocusEvent, useEffect, useRef } from 'react';
import { Box, Close, InputProps } from '@apia/theme';
import { TDateFormat, TModify } from '../../../types';
import { getDateFormat } from '../../../utils/date';
import { isChild } from '../../../utils/dom';
import CalModal, { ICalModal } from '../dialogs/modals/CalModal';
import IconInput, { IIconInput } from './IconInput';
import useCustomEvent from '../../../hooks/useCustomEvent';
import { useResponsiveTableContext } from '../generic/responsiveTable/ResponsiveTableContext';
import { getFocusSelector, getLabel, usePrevious } from '@apia/util';
import { ApiaUtil } from '@apia/components';

const DEFAULT_LOCALE = window.LANG_CODE;
const DEFAULT_MASK_PLACEHOLDER = '_';

const dateFormat: TDateFormat = getDateFormat();

const getMaskForDateFormat = (): string => {
  if (dateFormat === 'YYYY/MM/DD') return '9999/99/99';
  return '99/99/9999';
};
export type TDateProps = TModify<
  InputProps,
  {
    allowPickBeforeToday?: boolean;
    CalModalRenderer?: (
      props: ICalModal & React.RefAttributes<HTMLDivElement>,
    ) => React.ReactElement | null;
    deleteFiltersTimestamp?: number;
    error?: string | null;
    /**
     * Con esta propiedad se puede proveer una función que será utilizada para
     * determinar qué máscara debe utilizar el input
     */
    getCustomMask?: () => string;
    inputClassName?: string;
    isLoading?: boolean;
    onBlur?: (
      ev: FocusEvent<HTMLInputElement>,
    ) => void | boolean | string | Promise<boolean | string>;
    /**
     * Recibe la nueva fecha. En caso de devolver
     * false desde este método, el nuevo valor no
     * se persistirá.
     */
    onChange?: (newValue: string) => boolean | string | void;
    /**
     * Es llamado cada vez que alguien presiona el botón de eliminar fecha.
     */
    onDelete?: () => void;
    onError?: (error: string | null) => unknown;
    /**
     * Con esta propiedad se puede proveer una función que recibirá como
     * parámetro el valor actual del cmapo y debe devolver el valor a mostrar.
     */
    renderLabel?: (value: string) => string;
    value?: string;
  }
> &
  Pick<IIconInput, 'buttonProps'>;

const DateInput = React.forwardRef<HTMLInputElement, TDateProps>(
  (
    {
      allowPickBeforeToday,
      buttonProps: outerButtonProps,
      CalModalRenderer,
      className,
      error,
      getCustomMask,
      inputClassName,
      isLoading,
      onBlur,
      onChange,
      onDelete,
      onError,
      renderLabel,
      value: outerValue,
      ...props
    },
    ref,
  ) => {
    const id = React.useMemo(uniqueId, []);
    const [inputValue, setInputValue] = React.useState(outerValue ?? '');
    const [calValue, setCalValue] = React.useState<Date>();
    const boxRef = React.useRef<HTMLElement | null>(null);
    const { deleteFiltersTimestamp, ...otherProps } = props;

    const hasBlured = useRef(false);
    const { name } = useResponsiveTableContext();
    const [stateError, setError] = React.useState<string | null>(error ?? null);

    const previousName = usePrevious(name);
    if (previousName.current !== name) {
      setError(null);
    }

    const lastEmittedValue = React.useRef(outerValue ?? '');
    useUpdateEffect(() => {
      setInputValue(outerValue ?? '');
      lastEmittedValue.current = outerValue as string;
    }, [outerValue]);

    useUpdateEffect(() => {
      setError(error ?? null);
    }, [error]);

    useUpdateEffect(() => {
      setError(null);
      if (deleteFiltersTimestamp !== undefined) {
        setCalendarValue('');
      }
    }, [deleteFiltersTimestamp]);

    useUpdateEffect(() => {
      if (onError) onError(stateError);
      if (stateError) {
        // setInputValue('');
      }
      if (onError) onError(stateError ?? '');
    }, [stateError]);

    const shoutOnChange = React.useCallback(
      (newValue: string) => {
        lastEmittedValue.current = newValue;
        return onChange?.(newValue) ?? true;
      },
      [onChange],
    );

    const setCalendarValue = React.useCallback(
      (value: string, nocheck?: boolean, avoidShout?: boolean) => {
        if (value === '') {
          if (
            getMaskForDateFormat().replaceAll('9', DEFAULT_MASK_PLACEHOLDER) !==
            inputValue
          )
            void shoutOnChange('');
          setInputValue('');
          setCalValue(new Date());
          return;
        }

        let newDate = dayjs(value, dateFormat);
        if (!newDate.isValid()) newDate = dayjs(new Date(), dateFormat);

        const formattedDate = newDate.format(dateFormat);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (formattedDate !== inputValue && nocheck !== true) {
          void shoutOnChange('');
          setInputValue('');
          setError(window.MSG_INVALID_DATE);
        } else if (allowPickBeforeToday === false && newDate.isBefore(today)) {
          void shoutOnChange('');
          setInputValue('');
          setError(window.MSG_FEC_FIN_MAY_FEC_INI);
        } else {
          if (avoidShout) {
            setInputValue(formattedDate);
            setError(null);
            setCalValue(newDate.toDate());
          } else {
            const shoutResult = shoutOnChange(
              value === '' ? '' : formattedDate,
            );
            if (shoutResult === false) {
              setInputValue(shoutResult || '');
              setError(null);
              setCalValue(new Date());
            } else if (typeof shoutResult === 'string') {
              setInputValue(formattedDate);
              setError(shoutResult);
              setCalValue(new Date());
            } else {
              setInputValue(formattedDate);
              setError(null);
              setCalValue(newDate.toDate());
            }
          }
        }
      },
      [allowPickBeforeToday, inputValue, shoutOnChange],
    );

    const selectDate = React.useCallback(
      (val: Date): void => {
        const newValue = dayjs(val).format(dateFormat);
        setCalendarValue(newValue, true);
      },
      [setCalendarValue],
    );

    useEffect(() => {
      if (stateError !== '' && stateError !== null) {
        ApiaUtil.instance.notifications.notify({
          message: stateError,
          type: 'warning',
        });
      }
    }, [stateError]);
    const handleBlur = React.useCallback(
      (ev: React.FocusEvent<HTMLInputElement>) => {
        hasBlured.current = true;
        if (
          ev.target.value &&
          !dayjs(ev.target.value, dateFormat, true).isValid()
        ) {
          setInputValue('');
          onError?.(window.MSG_INVALID_DATE);
          return;
        }

        if (
          onBlur &&
          !isChild(ev.relatedTarget as HTMLElement, (current) => {
            return (
              current.id === `DateModal${id}` || current.id === `DateInput${id}`
            );
          })
        ) {
          const newEvent = {
            ...ev,
            target: ev.target.matches('input.dateInput')
              ? ev.target
              : (ev.target
                  .closest<HTMLInputElement>('.dateInput')
                  ?.querySelector<HTMLInputElement>(
                    'input',
                  ) as HTMLInputElement),
          };
          const actualBlur =
            onBlur instanceof Promise
              ? (onBlur as (ev: typeof newEvent) => Promise<boolean | string>)
              : (ev: typeof newEvent) =>
                  new Promise<boolean | string>((resolve) => {
                    const result = onBlur?.(ev);
                    resolve(result as boolean | string);
                  });

          void actualBlur(newEvent).then((result) => {
            if (typeof result === 'string') {
              setError(result);
            } else if (result === false) {
              setInputValue('');
            }
          });
        }
      },
      [id, onBlur, onError],
    );

    const inputProps = React.useMemo(
      () => ({
        ...otherProps,
        className: `dateInput ${inputClassName ?? ''}`,
        value: renderLabel ? renderLabel(inputValue) : inputValue,

        ref,
        onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
          setInputValue(ev.target.value);
          if (
            getMaskForDateFormat().replaceAll('9', DEFAULT_MASK_PLACEHOLDER) !==
              ev.target.value &&
            ev.target.value !== ''
          )
            setError(null);
        },
        onBlur: (ev: React.FocusEvent<HTMLInputElement>) => {
          if (
            !(
              props.readOnly ||
              getCustomMask ||
              document.querySelector('.overlay.screenLock')
            )
          )
            setCalendarValue(ev.target.value, false, true);
          if (hasBlured.current) {
            hasBlured.current = false;
          } else if (!document.querySelector('.overlay.screenLock')) {
            handleBlur(ev);
          }
        },
        onKeyDown: (ev: React.KeyboardEvent) => {
          if (ev.key === 'Enter') setCalendarValue(inputValue);
        },
      }),
      [
        otherProps,
        inputClassName,
        renderLabel,
        inputValue,
        ref,
        props.readOnly,
        getCustomMask,
        setCalendarValue,
        handleBlur,
      ],
    );

    const buttonProps = React.useMemo(
      () => ({
        disabled: props.disabled || props.readOnly,
        'aria-label': window.LBL_PICK_DATE,
        ...outerButtonProps,
      }),
      [outerButtonProps, props.disabled, props.readOnly],
    );

    const additionalButtons = React.useMemo(
      () => (
        <Close
          className="delete_date_button"
          onClick={() => {
            setCalendarValue('');
            onDelete?.();
            (
              document.querySelector(
                `#DateInput${id} input`,
              ) as HTMLInputElement
            )?.focus();
          }}
          title={getLabel('btnAiDelete').text}
          aria-label={getLabel('btnAiDelete').text}
          type="button"
        />
      ),
      [id, onDelete, setCalendarValue],
    );

    useCustomEvent(boxRef, 'clearError', () => {
      setError(null);
    });

    return (
      <>
        <Box
          {...window.getVariant('forms.dateInput')}
          className={`${className ?? ''} dateInput`}
          id={`DateInput${id}`}
          ref={boxRef}
          onBlur={(e: FocusEvent<HTMLInputElement, Element>) => {
            if (hasBlured.current) {
              hasBlured.current = false;
            } else if (!document.querySelector('.overlay.screenLock')) {
              handleBlur(e);
            }
          }}
        >
          <IconInput
            additionalButtons={
              inputValue &&
              inputValue !==
                getMaskForDateFormat().replaceAll(
                  '9',
                  DEFAULT_MASK_PLACEHOLDER,
                ) &&
              !props.readOnly &&
              !props.disabled
                ? additionalButtons
                : undefined
            }
            buttonProps={buttonProps}
            className={`${stateError !== null ? 'field__withError' : ''}`}
            icon={FaCalendarAlt}
            inputProps={inputProps}
            isLoading={isLoading}
            mask={getCustomMask ? getCustomMask() : getMaskForDateFormat()}
            maskPlaceholder={DEFAULT_MASK_PLACEHOLDER}
            onClick={(ev) => {
              const tooltip = ApiaUtil.instance.tooltips.open({
                attachToElement() {
                  return (ev.target as HTMLElement).closest<HTMLElement>(
                    'button',
                  )!;
                },
                attachToElementAnchorPoint: 'center',
                closeOnClick: false,
                closeOnClickOut: true,
                closeOnEscape: true,
                closeOnMouseLeaveTooltip: false,
                closeOnMouseLeaveAttachedElement: false,
                closeOnScrollOut: true,
                children: CalModalRenderer ? (
                  <CalModalRenderer
                    calValue={calValue}
                    locale={DEFAULT_LOCALE}
                    handleClickDay={(ev) => {
                      selectDate(ev);
                      setCalValue(ev);
                      tooltip.close();
                    }}
                  />
                ) : (
                  <CalModal
                    calValue={calValue}
                    locale={DEFAULT_LOCALE}
                    handleClickDay={(ev) => {
                      selectDate(ev);
                      setCalValue(ev);
                      tooltip.close();
                    }}
                  />
                ),
                initialFocusGetter(el) {
                  return el.querySelector(
                    `.react-calendar__tile--active, ${getFocusSelector()}`,
                  );
                },
                minSize: { width: 350, height: 310 },
              });
            }}
            readOnly={props.readOnly}
          />
        </Box>
      </>
    );
  },
);

export default DateInput;
